import React, { useEffect, useState } from "react";
import "./text.css";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import { Popover } from "@mui/material";
import { SketchPicker } from "react-color";
import { Type } from "lucide-react";

function Text({ onSelectText, textProps, onAlignLeft, onAlignRight }) {
  const [length, setLength] = useState({ x: 10, y: 10 });
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [shadowColorAnchorEl, setShadowColorAnchorEl] = useState(null);

  const hasSelection = textProps?.textBoxProps && Object.keys(textProps.textBoxProps).length > 0;
  const tp = textProps?.textBoxProps || {};

  useEffect(() => {
    if (textProps?.textBoxProps?.shadow) {
      setLength({
        x: textProps.textBoxProps.shadow.offsetX ?? 10,
        y: textProps.textBoxProps.shadow.offsetY ?? 10,
      });
    }
  }, [textProps?.textBoxProps?.shadow]);

  const handleColorClose = () => setColorAnchorEl(null);
  const handleShadowColorClose = () => setShadowColorAnchorEl(null);

  const onChangeColorComplete = (color) => {
    textProps?.onChangeTextFontColor?.(color.rgb, (properties) =>
      textProps?.onSetAllProperties?.(properties)
    );
  };
  const onChangeShadowColorComplete = (color) => {
    textProps?.onChangeTextBoxShadowColor?.(color.rgb, (properties) =>
      textProps?.onSetAllProperties?.(properties)
    );
  };
  const onChangeOffsetValues = (e) => {
    setLength((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const onShadowStateChange = (e) => {
    const checked = e.target.checked;
    textProps?.onChangeTextBoxShadowState?.(checked, (properties) =>
      textProps?.onSetAllProperties?.(properties)
    );
  };
  const onChangeParams = (e) => {
    const { name, value } = e.target;
    const num = Number(value);
    textProps?.onChangeParams?.({
      left: tp.left,
      top: tp.top,
      height: tp.height,
      width: tp.width,
      [name]: num,
    });
  };

  const fillDisplay = tp.fill
    ? (typeof tp.fill === "string" && tp.fill.startsWith("rgba")
      ? tp.fill
      : `rgba(${tp.fill?.r ?? 0},${tp.fill?.g ?? 0},${tp.fill?.b ?? 0},${tp.fill?.a ?? 1})`)
    : "#1e293b";

  return (
    <div className="de-text-panel">
      {/* Add text */}
      <section className="de-text-section">
        <h3 className="de-text-section-title">Add text</h3>
        <button
          className="de-text-add-btn"
          onClick={() => onSelectText("Enter Text", 50)}
        >
          <Type size={18} />
          Add text
        </button>
        <div className="de-text-presets">
          <button
            className="de-text-preset"
            onClick={() => onSelectText("Add Heading", 40)}
          >
            <span className="de-text-preset-label">Heading</span>
            <span className="de-text-preset-sample">Heading</span>
          </button>
          <button
            className="de-text-preset"
            onClick={() => onSelectText("Sub Heading", 30)}
          >
            <span className="de-text-preset-label">Subheading</span>
            <span className="de-text-preset-sample de-text-preset-sub">Subheading</span>
          </button>
          <button
            className="de-text-preset"
            onClick={() => onSelectText("Body text", 16)}
          >
            <span className="de-text-preset-label">Body</span>
            <span className="de-text-preset-sample de-text-preset-body">Body text</span>
          </button>
        </div>
      </section>

      {/* Formatting – Bold, Italic, Underline, Strikethrough */}
      <section className="de-text-section">
        <h3 className="de-text-section-title">Formatting</h3>
        {!hasSelection ? (
          <p className="de-text-hint">Select a text on the canvas to change style.</p>
        ) : (
          <>
            <div className="de-text-format-bar">
              <button
                type="button"
                className={`de-text-format-btn ${tp.fontWeight === "bold" ? "active" : ""}`}
                onClick={() => {
                  const next = tp.fontWeight === "bold" ? "normal" : "bold";
                  textProps?.onChangeFontWeight?.(next, (p) => textProps?.onSetAllProperties?.(p));
                }}
                title="Bold"
              >
                <FormatBoldIcon sx={{ fontSize: 20 }} />
              </button>
              <button
                type="button"
                className={`de-text-format-btn ${tp.fontStyle === "italic" ? "active" : ""}`}
                onClick={() => {
                  const next = tp.fontStyle === "italic" ? "normal" : "italic";
                  textProps?.onChangeFontStyle?.(next, (p) => textProps?.onSetAllProperties?.(p));
                }}
                title="Italic"
              >
                <FormatItalicIcon sx={{ fontSize: 20 }} />
              </button>
              <button
                type="button"
                className={`de-text-format-btn ${tp.underline ? "active" : ""}`}
                onClick={() => {
                  textProps?.onChangeUnderline?.(!tp.underline, (p) => textProps?.onSetAllProperties?.(p));
                }}
                title="Underline"
              >
                <FormatUnderlinedIcon sx={{ fontSize: 20 }} />
              </button>
              <button
                type="button"
                className={`de-text-format-btn ${tp.linethrough ? "active" : ""}`}
                onClick={() => {
                  textProps?.onChangeLinethrough?.(!tp.linethrough, (p) => textProps?.onSetAllProperties?.(p));
                }}
                title="Strikethrough"
              >
                <StrikethroughSIcon sx={{ fontSize: 20 }} />
              </button>
            </div>

            {/* Align */}
            <div className="de-text-row">
              <span className="de-text-label">Align</span>
              <div className="de-text-align-group">
                {[
                  { value: "left", Icon: FormatAlignLeftIcon },
                  { value: "center", Icon: FormatAlignCenterIcon },
                  { value: "right", Icon: FormatAlignRightIcon },
                ].map(({ value, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={`de-text-align-btn ${tp.textAlign === value ? "active" : ""}`}
                    onClick={() =>
                      textProps?.onChangeTextAlign?.(value, (p) => textProps?.onSetAllProperties?.(p))
                    }
                    title={value}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div className="de-text-row">
              <span className="de-text-label">Color</span>
              <button
                type="button"
                className="de-text-color-swatch"
                onClick={(e) => setColorAnchorEl(e.currentTarget)}
              >
                <span className="de-text-color-preview" style={{ background: fillDisplay }} />
                <span className="de-text-color-value">{fillDisplay}</span>
              </button>
              <Popover
                open={Boolean(colorAnchorEl)}
                anchorEl={colorAnchorEl}
                onClose={handleColorClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                <SketchPicker color={fillDisplay} onChangeComplete={onChangeColorComplete} />
              </Popover>
            </div>

            {/* Font size */}
            <div className="de-text-row">
              <span className="de-text-label">Size</span>
              <div className="de-text-size-control">
                <input
                  type="range"
                  min={8}
                  max={120}
                  value={tp.fontSize ?? 16}
                  onChange={(e) =>
                    textProps?.onChangeTextFontSize?.(e.target.value, (p) =>
                      textProps?.onSetAllProperties?.(p)
                    )
                  }
                  className="de-text-size-slider"
                />
                <span className="de-text-size-value">{tp.fontSize ?? 16}px</span>
              </div>
            </div>

            {/* Shadow toggle */}
            <div className="de-text-row de-text-row-check">
              <span className="de-text-label">Shadow</span>
              <label className="de-text-check-wrap">
                <input
                  type="checkbox"
                  checked={tp.isShadow ?? false}
                  onChange={onShadowStateChange}
                  className="de-text-check"
                />
                <span className="de-text-check-slider" />
              </label>
            </div>

            {tp.isShadow && (
              <>
                <div className="de-text-row">
                  <span className="de-text-label">Shadow blur</span>
                  <input
                    type="range"
                    min={0}
                    max={20}
                    value={tp.shadow?.size ?? 0}
                    onChange={(e) =>
                      textProps?.onChangeTextBoxShadowBlur?.(e.target.value, (p) =>
                        textProps?.onSetAllProperties?.(p)
                      )
                    }
                    className="de-text-size-slider"
                  />
                </div>
                <div className="de-text-row">
                  <span className="de-text-label">Shadow color</span>
                  <button
                    type="button"
                    className="de-text-color-swatch"
                    onClick={(e) => setShadowColorAnchorEl(e.currentTarget)}
                  >
                    <span
                      className="de-text-color-preview"
                      style={{ background: tp.shadow?.color || "#000" }}
                    />
                  </button>
                  <Popover
                    open={Boolean(shadowColorAnchorEl)}
                    anchorEl={shadowColorAnchorEl}
                    onClose={handleShadowColorClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    <SketchPicker
                      color={tp.shadow?.color || "#000"}
                      onChangeComplete={onChangeShadowColorComplete}
                    />
                  </Popover>
                </div>
                <div className="de-text-row de-text-double-input">
                  <div>
                    <span className="de-text-label">Offset X</span>
                    <input
                      type="number"
                      value={length.x}
                      name="x"
                      onChange={(e) => {
                        onChangeOffsetValues(e);
                        textProps?.onGetOffsetVal?.(
                          { x: e.target.value, y: length.y },
                          (p) => textProps?.onSetAllProperties?.(p)
                        );
                      }}
                      className="de-text-input"
                    />
                  </div>
                  <div>
                    <span className="de-text-label">Offset Y</span>
                    <input
                      type="number"
                      value={length.y}
                      name="y"
                      onChange={(e) => {
                        onChangeOffsetValues(e);
                        textProps?.onGetOffsetVal?.(
                          { y: e.target.value, x: length.x },
                          (p) => textProps?.onSetAllProperties?.(p)
                        );
                      }}
                      className="de-text-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Position & size */}
            <div className="de-text-section de-text-position-section">
              <h3 className="de-text-section-title">Position & size</h3>
              <div className="de-text-grid-2">
                <div className="de-text-field">
                  <label className="de-text-label">X</label>
                  <input
                    type="number"
                    name="left"
                    value={tp.left != null ? Math.round(Number(tp.left)) : ""}
                    onChange={onChangeParams}
                    className="de-text-input"
                  />
                </div>
                <div className="de-text-field">
                  <label className="de-text-label">Y</label>
                  <input
                    type="number"
                    name="top"
                    value={tp.top != null ? Math.round(Number(tp.top)) : ""}
                    onChange={onChangeParams}
                    className="de-text-input"
                  />
                </div>
                <div className="de-text-field">
                  <label className="de-text-label">Width</label>
                  <input
                    type="number"
                    name="width"
                    value={tp.width != null ? Math.round(Number(tp.width)) : ""}
                    onChange={onChangeParams}
                    className="de-text-input"
                  />
                </div>
                <div className="de-text-field">
                  <label className="de-text-label">Height</label>
                  <input
                    type="number"
                    name="height"
                    value={tp.height != null ? Math.round(Number(tp.height)) : ""}
                    onChange={onChangeParams}
                    className="de-text-input"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default Text;
